import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  /** @type {Element | null} */
  #listenerTarget = null;

  connectedCallback() {
    this.#listenerTarget = this.closest('.shopify-section, dialog');
    if (this.#listenerTarget) {
      this.#listenerTarget.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    } else {
      document.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    }
  }

  disconnectedCallback() {
    if (this.#listenerTarget) {
      this.#listenerTarget.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    } else {
      document.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    }
    this.#listenerTarget = null;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML, with fallback selectors
    let newProductPrice = this.dataset.blockId
      ? event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`)
      : null;

    if (!newProductPrice) {
      const productId = this.dataset.productId;
      if (productId) {
        const candidates = event.detail.data.html.querySelectorAll(`product-price[data-product-id="${productId}"]`);
        for (const candidate of candidates) {
          if (candidate.querySelector('[ref="priceContainer"]')) {
            newProductPrice = candidate;
            break;
          }
        }
      }
    }

    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
